import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Button from './ui/Button';
import Modal from './ui/Modal';
import ConfirmDialog from './ui/ConfirmDialog';
import { backfillSeoCandidatos, backfillSeoGenerar, backfillSeoAplicar, backfillSeoDescartar } from '../utils/productApi';
import { estadoDeFila, filtrarCandidatos, contarGeneradosSinAplicar, opcionesDeFiltro, resumenBackfill, FILTROS_VACIOS, hayFiltrosActivos, unionSeleccion, contarOcultos } from '../utils/backfillSeo';
import { tiposConfigurados } from '../utils/tiposProducto';
import './BackfillSeoIA.css';

// IA-1: backfill de SEO + descripción corta por lotes. Las sugerencias se GUARDAN en el backend
// (fila.borrador): al cerrar y volver siguen ahí, con su estado (Pendiente/Generado/Aplicado). Con
// buscador, filtros plegables, «ocultar aplicados» (por defecto) y resumen en una línea. Nada se escribe
// sin marcar «Aplicar». Por fila: Regenerar / Descartar. Arriba: «Descartar generados sin aplicar».
const chunk = (arr, n) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };
const FALTAS = [{ v: 'titulo', t: 'sin título' }, { v: 'meta', t: 'sin meta' }, { v: 'corta', t: 'desc. corta vacía o >150' }];
const fechaCorta = (d) => { try { return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit' }); } catch { return ''; } };

export default function BackfillSeoIA({ onClose, catalogoConfig = null }) {
  const [fase, setFase] = useState('cargando');   // 'cargando' | 'lista' | 'desactivada' | 'error'
  const [rows, setRows] = useState([]);
  const [errores, setErrores] = useState({});     // id -> mensaje de error de generación
  const [selGen, setSelGen] = useState(() => new Set());
  const [selApl, setSelApl] = useState(() => new Set());
  const [trabajando, setTrabajando] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filtros, setFiltros] = useState({ ...FILTROS_VACIOS });
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const [verSeleccionados, setVerSeleccionados] = useState(false);
  const [confirmarDescarte, setConfirmarDescarte] = useState(false);

  // Remate 6 (unificado): el fondo oscuro + el bloqueo real del scroll + Esc + clic fuera + foco atrapado
  // los da el componente Modal (sobre LayerOverlay/useScrollLock). Antes este modal tenía su propio overlay
  // sin portal → `position:fixed` quedaba atrapado por un ancestro con transform (no oscurecía) y el cuerpo
  // seguía desplazándose. Ya no hay manejo manual acá.

  const cargar = useCallback(async () => {
    setFase('cargando');
    try { const d = await backfillSeoCandidatos(); setRows(d.productos || []); setFase('lista'); }
    catch (e) { setFase(e.status === 403 ? 'desactivada' : 'error'); setMsg(e.message); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const labelTipo = useMemo(() => {
    const tipos = tiposConfigurados(catalogoConfig);
    const porArq = {}; tipos.forEach((t) => { if (t.arquetipo && !porArq[t.arquetipo]) porArq[t.arquetipo] = t.nombre; });
    const base = { prenda: 'Prendas', cosmetico: 'Belleza', consumible: 'Comestibles' };
    return (arq) => porArq[arq] || base[arq] || arq || '(sin tipo)';
  }, [catalogoConfig]);

  const opciones = useMemo(() => opcionesDeFiltro(rows), [rows]);
  const resumen = useMemo(() => resumenBackfill(rows), [rows]);
  const seleccion = useMemo(() => unionSeleccion(selGen, selApl), [selGen, selApl]);
  const filtradasBase = useMemo(() => filtrarCandidatos(rows, filtros), [rows, filtros]);
  const ocultos = useMemo(() => contarOcultos(seleccion, filtradasBase), [seleccion, filtradasBase]);
  // Remate 8: «Ver seleccionados» muestra SOLO los marcados (aunque los filtros los oculten).
  const filtradas = verSeleccionados ? rows.filter((f) => seleccion.has(f.id)) : filtradasBase;
  const generadosSinAplicar = contarGeneradosSinAplicar(rows);
  const setFiltro = (k, v) => { setFiltros((f) => ({ ...f, [k]: v })); setVerSeleccionados(false); };
  const limpiarFiltros = () => { setFiltros({ ...FILTROS_VACIOS }); setVerSeleccionados(false); };
  const toggle = (set, setSet, id) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); setSet(n); };

  const marcarVisiblesParaGenerar = () => {
    const ids = filtradas.filter((f) => estadoDeFila(f) === 'pendiente').map((f) => f.id);
    const todos = ids.every((id) => selGen.has(id)) && ids.length > 0;
    setSelGen(todos ? new Set() : new Set(ids));
  };
  const marcarGeneradosParaAplicar = () => {
    const ids = filtradas.filter((f) => estadoDeFila(f) === 'generado').map((f) => f.id);
    setSelApl(new Set(ids));
  };

  // Genera un lote de ids y guarda el borrador. Reutilizado por «Generar (N)» y por «Regenerar» por fila.
  const generarIds = async (ids) => {
    if (!ids.length) return;
    setTrabajando(true); setMsg(null);
    try {
      const porId = {}; const errs = {}; const okIds = [];
      for (const grupo of chunk(ids, 50)) {
        const d = await backfillSeoGenerar(grupo);
        for (const r of (d.resultados || [])) {
          if (r.error) errs[r.id] = r.error;
          else { porId[r.id] = { estado: 'generado', generadoEn: r.generadoEn, titulo_seo: r.titulo_seo, meta_descripcion: r.meta_descripcion, descripcion_corta: r.descripcion_corta, en_que_me_base: r.en_que_me_base }; okIds.push(r.id); }
        }
      }
      setRows((rs) => rs.map((f) => (porId[f.id] ? { ...f, borrador: porId[f.id] } : f)));
      setErrores((e) => { const n = { ...e }; okIds.forEach((id) => delete n[id]); return { ...n, ...errs }; });
      setSelApl((s) => { const n = new Set(s); okIds.forEach((id) => n.add(id)); return n; });
      setSelGen((s) => { const n = new Set(s); ids.forEach((id) => n.delete(id)); return n; });
    } catch (e) { setMsg(e.message || 'No se pudo generar'); }
    finally { setTrabajando(false); }
  };
  const generar = () => generarIds([...selGen]);
  const regenerarUno = (id) => generarIds([id]);

  const aplicar = async () => {
    const items = [...selApl]
      .map((id) => {
        const f = rows.find((x) => x.id === id);
        if (!f || !f.borrador || f.borrador.estado !== 'generado') return null;
        const it = { id, titulo_seo: f.borrador.titulo_seo, meta_descripcion: f.borrador.meta_descripcion };
        if (f.needsShort && f.borrador.descripcion_corta) it.descripcion_corta = f.borrador.descripcion_corta;
        return it;
      })
      .filter(Boolean);
    if (!items.length) return;
    setTrabajando(true); setMsg(null);
    try {
      let aplicados = 0; const conError = [];
      for (const grupo of chunk(items, 50)) {
        const d = await backfillSeoAplicar(grupo);
        for (const r of (d.resultados || [])) { if (r.ok) aplicados += 1; else conError.push(r.id); }
      }
      const okIds = new Set(items.map((i) => i.id).filter((id) => !conError.includes(id)));
      setRows((rs) => rs.map((f) => (okIds.has(f.id) ? { ...f, borrador: { ...f.borrador, estado: 'aplicado', aplicadoEn: new Date().toISOString() } } : f)));
      setSelApl(new Set()); setSelGen(new Set());
      setMsg(`${aplicados} aplicado(s)${conError.length ? ` · ${conError.length} con error` : ''}.`);
    } catch (e) { setMsg(e.message || 'No se pudo aplicar'); }
    finally { setTrabajando(false); }
  };

  // Remate 9: descartar borra el borrador GENERADO (la fila vuelve a Pendiente). Por fila o en lote.
  const descartarIds = async (ids) => {
    if (!ids.length) return;
    setTrabajando(true); setMsg(null);
    try {
      await backfillSeoDescartar(ids);
      const quitar = new Set(ids);
      setRows((rs) => rs.map((f) => (quitar.has(f.id) && estadoDeFila(f) !== 'aplicado' ? { ...f, borrador: null } : f)));
      setErrores((e) => { const n = { ...e }; ids.forEach((id) => delete n[id]); return n; });
      setSelApl((s) => { const n = new Set(s); ids.forEach((id) => n.delete(id)); return n; });
    } catch (e) { setMsg(e.message || 'No se pudo descartar'); }
    finally { setTrabajando(false); }
  };
  const descartarUno = (id) => descartarIds([id]);
  const confirmarDescartarGenerados = () => {
    setConfirmarDescarte(false);
    descartarIds(rows.filter((f) => estadoDeFila(f) === 'generado').map((f) => f.id));
  };

  const chip = (f) => {
    const est = estadoDeFila(f);
    if (errores[f.id]) return <span className="bf-chip bf-chip--err">Error</span>;
    if (est === 'aplicado') return <span className="bf-chip bf-chip--ok">Aplicado ✓ {f.borrador.aplicadoEn ? fechaCorta(f.borrador.aplicadoEn) : ''}</span>;
    if (est === 'generado') return <span className="bf-chip bf-chip--gen">Generado {f.borrador.generadoEn ? fechaCorta(f.borrador.generadoEn) : ''}</span>;
    return <span className="bf-chip">Pendiente</span>;
  };

  return (
    <Modal open onClose={onClose} title="SEO con IA — por lotes" ancho={980}>
      {fase === 'cargando' && <p className="bf-msg">Cargando productos…</p>}
        {fase === 'desactivada' && <p className="bf-msg">La IA de la ficha está desactivada. Actívala en Configuración para usar el backfill.</p>}
        {fase === 'error' && <p className="bf-msg bf-err">No se pudieron cargar los candidatos. {msg}</p>}

        {fase === 'lista' && (
          <>
            {/* Remate 7: encabezado compacto — buscador + «Filtros ▾» + resumen en una línea. */}
            <div className="bf-topbar">
              <input className="bf-input bf-buscar" placeholder="Buscar por nombre o SKU…" value={filtros.q} onChange={(e) => setFiltro('q', e.target.value)} aria-label="Buscar por nombre o SKU" />
              <button type="button" className="bf-filtros-toggle" aria-expanded={filtrosAbiertos} onClick={() => setFiltrosAbiertos((v) => !v)}>
                Filtros {filtrosAbiertos ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
              </button>
              {(hayFiltrosActivos(filtros) || verSeleccionados) && (
                <button type="button" className="bf-limpiar" onClick={limpiarFiltros}>Limpiar filtros</button>
              )}
            </div>

            <p className="bf-resumen">
              <strong>{resumen.total}</strong> productos · <strong>{resumen.sinSeo}</strong> sin SEO · <strong>{resumen.generadosSinAplicar}</strong> generados sin aplicar · <strong>{resumen.aplicados}</strong> aplicados
            </p>

            {filtrosAbiertos && (
              <div className="bf-filtros">
                <select className="bf-input" value={filtros.tipo} onChange={(e) => setFiltro('tipo', e.target.value)} aria-label="Filtrar por tipo">
                  <option value="">Tipo: todos</option>
                  {opciones.tipos.map((a) => <option key={a} value={a}>{labelTipo(a)}</option>)}
                </select>
                <select className="bf-input" value={filtros.categoria} onChange={(e) => setFiltro('categoria', e.target.value)} aria-label="Filtrar por categoría">
                  <option value="">Categoría: todas</option>
                  {opciones.categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select className="bf-input" value={filtros.marca} onChange={(e) => setFiltro('marca', e.target.value)} aria-label="Filtrar por marca">
                  <option value="">Marca: todas</option>
                  {opciones.marcas.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <select className="bf-input" value={filtros.coleccion} onChange={(e) => setFiltro('coleccion', e.target.value)} aria-label="Filtrar por colección">
                  <option value="">Colección: todas</option>
                  {opciones.colecciones.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select className="bf-input" value={filtros.falta} onChange={(e) => setFiltro('falta', e.target.value)} aria-label="Filtrar por falta">
                  <option value="">Falta: cualquiera</option>
                  {FALTAS.map((f) => <option key={f.v} value={f.v}>{f.t}</option>)}
                </select>
                <label className="bf-ocultar"><input type="checkbox" checked={filtros.ocultarAplicados} onChange={(e) => setFiltro('ocultarAplicados', e.target.checked)} /> Ocultar aplicados</label>
              </div>
            )}

            {/* Remate 8: selección oculta por los filtros. */}
            {seleccion.size > 0 && (
              <p className="bf-selinfo">
                <strong>{seleccion.size}</strong> seleccionado(s){ocultos > 0 ? ` (${ocultos} oculto(s) por los filtros)` : ''}
                {' · '}
                <button type="button" className="bf-link" onClick={() => setVerSeleccionados((v) => !v)}>
                  {verSeleccionados ? 'Ver todos' : 'Ver seleccionados'}
                </button>
              </p>
            )}

            <div className="bf-acc">
              <Button size="sm" variant="tertiary" onClick={marcarVisiblesParaGenerar} disabled={trabajando || !filtradas.length}>Marcar todos los visibles</Button>
              <Button size="sm" variant="secondary" onClick={generar} loading={trabajando} disabled={!selGen.size}>Generar SEO ({selGen.size})</Button>
              <Button size="sm" variant="tertiary" onClick={marcarGeneradosParaAplicar} disabled={trabajando || !filtradas.some((f) => estadoDeFila(f) === 'generado')}>Aplicar todos los generados</Button>
              <Button size="sm" variant="primary" onClick={aplicar} loading={trabajando} disabled={!selApl.size}>Aplicar marcados ({selApl.size})</Button>
              <Button size="sm" variant="tertiary" onClick={() => setConfirmarDescarte(true)} disabled={trabajando || !generadosSinAplicar}>Descartar generados sin aplicar ({generadosSinAplicar})</Button>
            </div>
            {msg && <p className="bf-msg bf-ok">{msg}</p>}

            <div className="bf-tabla-wrap">
              <table className="bf-tabla">
                <thead>
                  <tr><th>Generar</th><th>Producto</th><th>Estado</th><th>SEO actual</th><th>SEO generado</th><th>Aplicar</th></tr>
                </thead>
                <tbody>
                  {filtradas.map((f) => {
                    const est = estadoDeFila(f);
                    const b = f.borrador;
                    return (
                      <tr key={f.id}>
                        <td><input type="checkbox" checked={selGen.has(f.id)} disabled={trabajando || est !== 'pendiente'} onChange={() => toggle(selGen, setSelGen, f.id)} aria-label={`Generar ${f.name}`} /></td>
                        <td>
                          <div className="bf-nombre">{f.name}</div>
                          <div className="bf-cats">{f.sku ? `${f.sku} · ` : ''}{labelTipo(f.arquetipo)}{(f.categorias || []).length ? ` · ${f.categorias.join(' / ')}` : ''}{f.marca ? ` · ${f.marca}` : ''}</div>
                        </td>
                        <td>{chip(f)}{errores[f.id] && <div className="bf-err">{errores[f.id]}</div>}</td>
                        <td className="bf-actual">
                          <div>{f.seoTitleActual ? f.seoTitleActual : <em>título vacío</em>}</div>
                          <div>{f.seoMetaActual ? f.seoMetaActual : <em>meta vacía</em>}</div>
                          {f.needsShort && <div className="bf-short-flag">{f.shortLen === 0 ? 'desc. corta vacía' : `desc. corta ${f.shortLen} car. (>150)`}</div>}
                        </td>
                        <td className="bf-gen">
                          {!b && <span className="bf-pend">—</span>}
                          {b && (
                            <>
                              <div className="bf-gen-t"><strong>{b.titulo_seo}</strong> <span className="bf-len">({(b.titulo_seo || '').length})</span></div>
                              <div className="bf-gen-m">{b.meta_descripcion} <span className="bf-len">({(b.meta_descripcion || '').length})</span></div>
                              {f.needsShort && b.descripcion_corta && <div className="bf-gen-s"><em>Desc. corta:</em> {b.descripcion_corta} <span className="bf-len">({b.descripcion_corta.length})</span></div>}
                              {b.en_que_me_base && <div className="bf-base">{b.en_que_me_base}</div>}
                              {est === 'generado' && (
                                <div className="bf-fila-acc">
                                  <button type="button" className="bf-link" disabled={trabajando} onClick={() => regenerarUno(f.id)}>Regenerar</button>
                                  <button type="button" className="bf-link bf-link--danger" disabled={trabajando} onClick={() => descartarUno(f.id)}>Descartar</button>
                                </div>
                              )}
                            </>
                          )}
                        </td>
                        <td><input type="checkbox" checked={selApl.has(f.id)} disabled={trabajando || est !== 'generado'} onChange={() => toggle(selApl, setSelApl, f.id)} aria-label={`Aplicar ${f.name}`} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="bf-foot">Se procesa en lotes de 50. Las sugerencias quedan guardadas aunque cierres el modal.</p>
          </>
        )}
      <ConfirmDialog
        open={confirmarDescarte}
        title="Descartar generados sin aplicar"
        confirmLabel="Descartar"
        onConfirm={confirmarDescartarGenerados}
        onCancel={() => setConfirmarDescarte(false)}
      >
        Se borrarán {generadosSinAplicar} sugerencia(s) generada(s) y sin aplicar. Las ya aplicadas no se tocan.
      </ConfirmDialog>
    </Modal>
  );
}
